/**
 * CUSTOMIZATION: Linked Options Logic
 * 
 * This script ensures that when a user selects a primary option (like "Form": Oil),
 * only the secondary options (like "Size") that are actually available for that 
 * primary option will be displayed. Unavailable options are hidden.
 * 
 * It listens for changes on the variant picker and filters the DOM accordingly.
 */

document.addEventListener('DOMContentLoaded', () => {
  // Initialize the linked options logic for all variant pickers on the page
  const variantPickers = document.querySelectorAll('variant-picker');

  variantPickers.forEach(picker => {
    initLinkedOptions(picker);

    // Listen for changes on the picker inputs (triggers immediately on click)
    picker.addEventListener('change', () => {
      applyLinkedOptionsLogic(picker);
    });

    // Listen for the theme's custom update event (triggers after server fetch replaces HTML)
    picker.addEventListener('variant:update', () => {
      applyLinkedOptionsLogic(picker);
    });
  });
});

/**
 * Initialize the logic when the picker is first found
 */
function initLinkedOptions(picker) {
  // Check if we already have the variants JSON data in the DOM
  const productId = picker.dataset.productId;
  const sectionId = picker.dataset.sectionId;
  let jsonScript = document.getElementById(`ProductVariants-${sectionId}-${productId}`);

  if (!jsonScript) {
    // If the JSON script is missing, maybe it was replaced and the ID changed.
    // Let's try to find it generically.
    const genericScript = picker.querySelector('script[type="application/json"][id^="ProductVariants-"]');
    if (!genericScript) {
      console.warn('Custom Linked Options: No variants JSON found for this product.');
      return;
    }
    jsonScript = genericScript;
  }
  
  try {
    const variants = JSON.parse(jsonScript.textContent);
    picker.dataset.allVariants = JSON.stringify(variants);
    // Apply logic immediately on load to hide unavailable options
    applyLinkedOptionsLogic(picker);
  } catch (e) {
    console.error('Custom Linked Options: Failed to parse variants JSON.', e);
  }
}

/**
 * Core logic to filter and hide unavailable options
 * Exposed to window so variant-picker.js can pre-filter new HTML before morphing.
 */
window.applyLinkedOptionsLogic = function(picker, variantsRawOverride = null) {
  let variantsRaw = variantsRawOverride || picker.dataset.allVariants;
  
  if (!variantsRaw) {
    // If morphdom stripped the dataset attribute, recover it from the JSON script
    const jsonScript = picker.querySelector('script[type="application/json"][id^="ProductVariants-"]');
    if (jsonScript) {
      variantsRaw = jsonScript.textContent;
      picker.dataset.allVariants = variantsRaw;
    }
  }

  if (!variantsRaw) return false;
  const variants = JSON.parse(variantsRaw);
  const fieldsets = Array.from(picker.querySelectorAll('.variant-option'));

  if (fieldsets.length < 2) {
    // Linked options only make sense if there's more than one option (e.g. Form AND Size)
    return;
  }

  // Get currently selected values
  const selectedValues = [];
  fieldsets.forEach((fieldset) => {
    // Handle radio buttons (swatches/buttons)
    const checkedRadio = fieldset.querySelector('input[type="radio"]:checked');
    if (checkedRadio) {
      selectedValues.push(checkedRadio.value);
    } else {
      // Handle dropdowns
      const select = fieldset.querySelector('select');
      if (select) {
        selectedValues.push(select.value);
      } else {
        selectedValues.push(null);
      }
    }
  });

  // We only filter Option 2 (and Option 3) based on Option 1's selection
  const option1Value = selectedValues[0];

  if (!option1Value) return;

  // Find all variants that match Option 1
  const availableVariants = variants.filter(v => v.option1 === option1Value);

  // Extract available Option 2 values for this Option 1
  const availableOption2Values = availableVariants.map(v => v.option2).filter(Boolean);

  // Now, update the DOM for Option 2 (fieldsets[1])
  const option2Fieldset = fieldsets[1];

  let needsNewSelection = false;
  let firstAvailableInput = null;

  // Handle radio buttons in Option 2
  const radios = option2Fieldset.querySelectorAll('input[type="radio"]');
  radios.forEach(radio => {
    const label = radio.closest('label');
    if (availableOption2Values.includes(radio.value)) {
      // Option is available
      if (label) label.style.display = '';
      if (!firstAvailableInput) firstAvailableInput = radio;
    } else {
      // Option is NOT available, hide it completely
      if (label) label.style.display = 'none';

      // If this hidden option is currently selected, we need to change the selection
      if (radio.checked) {
        needsNewSelection = true;
      }
    }
  });

  // Handle select dropdowns in Option 2 (if the theme is configured to use dropdowns instead of buttons)
  const select = option2Fieldset.querySelector('select');
  if (select) {
    const options = select.querySelectorAll('option');
    options.forEach(option => {
      if (availableOption2Values.includes(option.value)) {
        option.style.display = '';
        if (!firstAvailableInput) firstAvailableInput = option;
      } else {
        option.style.display = 'none';
        if (option.selected) {
          needsNewSelection = true;
        }
      }
    });
  }

  // If the user's previously selected Option 2 is no longer valid, auto-select the first valid one
  if (needsNewSelection && firstAvailableInput) {
    if (firstAvailableInput.tagName === 'INPUT') {
      firstAvailableInput.checked = true;
      // Trigger a change event so the native variant-picker component updates the URL and fetches the new product info
      firstAvailableInput.dispatchEvent(new Event('change', { bubbles: true }));
    } else if (firstAvailableInput.tagName === 'OPTION') {
      select.value = firstAvailableInput.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
    return true;
  }
  return false;
}
